import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Link } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { LayersIcon, SearchIcon, FilterIcon, ChevronDownIcon, PlusIcon, RefreshCwIcon, DownloadIcon, TrashIcon, EditIcon, EyeIcon, MoreHorizontalIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, CalendarIcon, ClockIcon, TagIcon, FileTextIcon, UserIcon, BuildingIcon, BarChart2Icon, InfoIcon, HelpCircleIcon, SaveIcon, XIcon, UploadIcon, ArrowLeftIcon, ArrowRightIcon, ImageIcon } from 'lucide-react';
// Service Type for TypeScript
interface Service {
  id: string;
  name: string;
  description: string;
  mda: {
    id: string;
    name: string;
    acronym: string;
  };
  category: string;
  fee: number;
  processingTime: string;
  requirements: string[];
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  applicationsCount: number;
}
// Mock data for services
const generateMockServices = (count: number, mdaId?: string) => {
  const statuses = ['Active', 'Inactive', 'Draft', 'Under Review', 'Archived'];
  const categories = ['Business', 'Tax', 'Permits', 'Licenses', 'Certificates', 'Registration', 'Approvals'];
  const mdas = ['Ministry of Finance', 'Ministry of Lands and Surveys', 'Ministry of Commerce and Industry', 'Federal Road Safety Commission', 'Ministry of Education'];
  const mockData = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const category = categories[Math.floor(Math.random() * categories.length)];
    const mda = mdas[Math.floor(Math.random() * mdas.length)];
    const daysAgo = Math.floor(Math.random() * 365);
    const createdDate = new Date(now);
    createdDate.setDate(createdDate.getDate() - daysAgo);
    const lastUpdated = new Date(createdDate);
    lastUpdated.setDate(lastUpdated.getDate() + Math.floor(Math.random() * daysAgo));
    let name = '';
    let description = '';
    let processingTime = Math.floor(Math.random() * 14) + 1;
    let fee = Math.floor(Math.random() * 50000) + 1000;
    let applicationCount = Math.floor(Math.random() * 1000);
    // Generate name and description based on MDA and category
    if (mda === 'Ministry of Finance') {
      if (category === 'Tax') {
        name = ['Tax Clearance Certificate', 'Tax Identification Number (TIN) Registration', 'Company Income Tax Filing', 'Personal Income Tax Assessment', 'Value Added Tax (VAT) Registration'][Math.floor(Math.random() * 5)];
        description = ['Obtain a tax clearance certificate for your business or personal use.', 'Register for a Tax Identification Number (TIN) for your business or personal use.', 'File your company income tax returns for the financial year.', 'Get an assessment of your personal income tax liability.', 'Register your business for Value Added Tax (VAT) collection and remittance.'][Math.floor(Math.random() * 5)];
      } else if (category === 'Business') {
        name = ['Business Permit Renewal', 'Financial Statement Filing', 'Annual Returns Submission', 'Tax Exemption Certificate', 'Tax Refund Application'][Math.floor(Math.random() * 5)];
        description = ['Renew your business permit for continued operation.', 'Submit your financial statements for regulatory compliance.', 'File your annual returns as required by law.', 'Apply for a tax exemption certificate for eligible businesses.', 'Request a refund for overpaid taxes.'][Math.floor(Math.random() * 5)];
      } else {
        name = ['Financial Compliance Certificate', 'Revenue Collection License', 'Treasury Single Account Registration', 'Government Contractor Registration', 'Fiscal Policy Compliance Certificate'][Math.floor(Math.random() * 5)];
        description = ['Obtain a certificate confirming compliance with financial regulations.', 'Apply for a license to collect revenue on behalf of the government.', 'Register for the Treasury Single Account system.', 'Register as a government contractor for financial projects.', 'Get certified for compliance with current fiscal policies.'][Math.floor(Math.random() * 5)];
      }
    } else if (mda === 'Ministry of Lands and Surveys') {
      if (category === 'Certificates') {
        name = ['Certificate of Occupancy', 'Land Title Registration', 'Property Ownership Transfer', 'Land Use Permit', 'Building Plan Approval'][Math.floor(Math.random() * 5)];
        description = ['Obtain a certificate of occupancy for your land.', 'Register the title of your land for legal ownership.', 'Transfer property ownership from one entity to another.', 'Get a permit for specific land use purposes.', 'Get approval for your building construction plans.'][Math.floor(Math.random() * 5)];
      } else if (category === 'Permits') {
        name = ['Construction Permit', 'Land Survey Approval', 'Property Demolition Permit', 'Land Conversion Permit', 'Fencing Permit'][Math.floor(Math.random() * 5)];
        description = ['Obtain a permit to construct a building on your land.', 'Get approval for land survey plans and documents.', 'Apply for permission to demolish existing structures.', 'Convert land from one use category to another.', 'Get permission to erect a fence around your property.'][Math.floor(Math.random() * 5)];
      } else {
        name = ['Land Verification Service', 'Property Valuation Service', 'Cadastral Survey Plan', 'Land Dispute Resolution', 'Property Tax Assessment'][Math.floor(Math.random() * 5)];
        description = ['Verify the authenticity and status of land documents.', 'Get an official valuation of your property.', 'Obtain a detailed cadastral survey plan for your land.', 'Resolve land boundary and ownership disputes.', 'Get an assessment of property tax obligations.'][Math.floor(Math.random() * 5)];
      }
    } else if (mda === 'Ministry of Commerce and Industry') {
      if (category === 'Registration') {
        name = ['Business Name Registration', 'Limited Liability Company Registration', 'Trademark Registration', 'Patent Registration', 'Cooperative Society Registration'][Math.floor(Math.random() * 5)];
        description = ['Register a business name for your enterprise.', 'Register a limited liability company with the government.', 'Register a trademark to protect your brand.', 'Register a patent for your invention or innovation.', 'Register a cooperative society for collective business.'][Math.floor(Math.random() * 5)];
      } else if (category === 'Licenses') {
        name = ['Import License', 'Export License', 'Manufacturing License', 'Trade Fair Participation License', 'Market Stall Allocation'][Math.floor(Math.random() * 5)];
        description = ['Obtain a license to import goods into the country.', 'Get a license to export local products to international markets.', 'Apply for a license to manufacture products.', 'Get permission to participate in government-sponsored trade fairs.', 'Apply for allocation of a market stall for your business.'][Math.floor(Math.random() * 5)];
      } else {
        name = ['Business Advisory Service', 'Market Research Report', 'SME Development Program', 'Export Promotion Service', 'Industrial Dispute Resolution'][Math.floor(Math.random() * 5)];
        description = ['Get professional advice for your business growth and development.', 'Obtain market research reports for business planning.', 'Participate in programs designed to develop small and medium enterprises.', 'Access services to promote export of local products.', 'Resolve industrial disputes through government mediation.'][Math.floor(Math.random() * 5)];
      }
    } else if (mda === 'Federal Road Safety Commission') {
      if (category === 'Licenses') {
        name = ["Driver's License Issuance", "Driver's License Renewal", "Commercial Driver's License", "International Driver's Permit", 'Driving School Accreditation'][Math.floor(Math.random() * 5)];
        description = ["Apply for a new driver's license.", "Renew your existing driver's license.", 'Obtain a license for commercial driving.', 'Get an international permit for driving abroad.', 'Get accreditation for operating a driving school.'][Math.floor(Math.random() * 5)];
      } else if (category === 'Registration') {
        name = ['Vehicle Registration', 'Number Plate Issuance', 'Change of Vehicle Ownership', 'Vehicle License Renewal', 'Fleet Operator Registration'][Math.floor(Math.random() * 5)];
        description = ['Register your vehicle with the appropriate authorities.', 'Get official number plates for your vehicle.', 'Transfer vehicle ownership to a new owner.', 'Renew your vehicle license.', 'Register as a fleet operator for multiple vehicles.'][Math.floor(Math.random() * 5)];
      } else {
        name = ['Road Worthiness Certificate', 'Transport Operator License', 'Heavy Duty Vehicle Permit', 'Special Number Plate Application', 'Vehicle Inspection Service'][Math.floor(Math.random() * 5)];
        description = ['Obtain a certificate confirming your vehicle is road worthy.', 'Get a license to operate as a transport service provider.', 'Apply for a permit to operate heavy duty vehicles.', 'Request special or personalized number plates.', 'Get your vehicle inspected for safety and compliance.'][Math.floor(Math.random() * 5)];
      }
    } else if (mda === 'Ministry of Education') {
      if (category === 'Registration') {
        name = ['School Registration', 'Teacher Registration', 'Student Enrollment', 'Educational Institution Accreditation', 'Scholarship Program Registration'][Math.floor(Math.random() * 5)];
        description = ['Register a new school with the Ministry of Education.', 'Register as a qualified teacher in the national database.', 'Enroll students in government schools or programs.', 'Get accreditation for your educational institution.', 'Register for government scholarship programs.'][Math.floor(Math.random() * 5)];
      } else if (category === 'Certificates') {
        name = ['Transcript Verification', 'Certificate Authentication', 'Educational Qualification Assessment', 'School Leaving Certificate', 'Professional Teaching Certificate'][Math.floor(Math.random() * 5)];
        description = ['Verify the authenticity of academic transcripts.', 'Authenticate educational certificates and diplomas.', 'Get an assessment of your educational qualifications.', 'Obtain an official school leaving certificate.', 'Apply for a professional teaching certificate.'][Math.floor(Math.random() * 5)];
      } else {
        name = ['Curriculum Development Service', 'Educational Research Permit', 'School Inspection Service', 'Educational Material Approval', 'Exchange Program Application'][Math.floor(Math.random() * 5)];
        description = ['Get assistance with curriculum development for schools.', 'Apply for a permit to conduct educational research.', 'Request inspection of schools for quality assurance.', 'Get approval for educational materials and textbooks.', 'Apply for international educational exchange programs.'][Math.floor(Math.random() * 5)];
      }
    } else {
      name = `Generic Service ${i + 1}`;
      description = `Description for Generic Service ${i + 1}`;
    }
    // Only include services for the specified MDA if mdaId is provided
    if (!mdaId || mda === mdaId) {
      mockData.push({
        id: `service-${i}-${Date.now()}`,
        name,
        description,
        status,
        category,
        mda,
        processingTime,
        fee,
        applicationCount,
        createdDate: createdDate.toISOString(),
        lastUpdated: lastUpdated.toISOString(),
        requirements: ["Valid identification (National ID, Driver's License, or International Passport)", 'Proof of address (Utility bill not older than 3 months)', 'Passport photograph (taken within the last 6 months)', 'Completed application form', 'Payment of application fee'],
        steps: ['Complete and submit the online application form', 'Pay the required fee', 'Upload the required documents', 'Attend in-person verification (if applicable)', 'Receive confirmation and track application status']
      });
    }
  }
  return mockData;
};
const ServiceManagement: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [services, setServices] = useState<any[]>([]);
  const [filteredServices, setFilteredServices] = useState<any[]>([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedService, setEditedService] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState('');
  const [showNewServiceModal, setShowNewServiceModal] = useState(false);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    category: 'Business',
    status: 'Draft',
    mda: '',
    processingTime: 5,
    fee: 5000,
    requirements: [''],
    steps: ['']
  });
  const [isSaving, setIsSaving] = useState(false);
  const [serviceStats, setServiceStats] = useState({
    total: 0,
    active: 0,
    draft: 0,
    popular: '',
    averageFee: 0
  });
  const {
    hasPermission,
    isSuperAdmin,
    getMdaId,
    getMdaName
  } = useAdminAuth();
  const canCreateService = hasPermission('services:create');
  const canEditService = hasPermission('services:edit');
  const canDeleteService = hasPermission('services:delete');
  // Load services
  useEffect(() => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      let allServices = [];
      // If super admin, load all services
      // If MDA admin, only load services for their MDA
      if (isSuperAdmin()) {
        allServices = generateMockServices(50);
      } else {
        const mdaName = getMdaName();
        allServices = generateMockServices(20, mdaName);
      }
      setServices(allServices);
      setFilteredServices(allServices);
      // Calculate service stats
      const activeServices = allServices.filter((s: any) => s.status === 'Active').length;
      const draftServices = allServices.filter((s: any) => s.status === 'Draft').length;
      // Find most popular service
      let mostPopular = allServices[0];
      allServices.forEach((service: any) => {
        if (service.applicationCount > (mostPopular?.applicationCount || 0)) {
          mostPopular = service;
        }
      });
      // Calculate average fee
      const totalFee = allServices.reduce((sum: number, service: any) => sum + service.fee, 0);
      const averageFee = Math.round(totalFee / allServices.length);
      setServiceStats({
        total: allServices.length,
        active: activeServices,
        draft: draftServices,
        popular: mostPopular?.name || 'None',
        averageFee
      });
      setIsLoading(false);
    }, 1000);
  }, [isSuperAdmin, getMdaId, getMdaName]);
  // Apply filters
  useEffect(() => {
    let filtered = [...services];
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(s => s.category === categoryFilter);
    }
    // Apply active filter
    if (activeFilter === 'active') {
      filtered = filtered.filter(s => s.status === 'Active');
    } else if (activeFilter === 'draft') {
      filtered = filtered.filter(s => s.status === 'Draft');
    } else if (activeFilter === 'popular') {
      filtered.sort((a, b) => b.applicationCount - a.applicationCount);
      filtered = filtered.slice(0, 10);
    }
    // Apply search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => s.name.toLowerCase().includes(query) || s.description.toLowerCase().includes(query) || s.category.toLowerCase().includes(query) || s.mda.toLowerCase().includes(query));
    }
    // Apply sort order
    filtered.sort((a, b) => {
      if (sortOrder === 'newest') {
        return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
      } else if (sortOrder === 'oldest') {
        return new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
      } else if (sortOrder === 'name-asc') {
        return a.name.localeCompare(b.name);
      } else if (sortOrder === 'name-desc') {
        return b.name.localeCompare(a.name);
      } else if (sortOrder === 'fee-high') {
        return b.fee - a.fee;
      } else if (sortOrder === 'fee-low') {
        return a.fee - b.fee;
      }
      return 0;
    });
    setFilteredServices(filtered);
  }, [services, activeFilter, searchQuery, statusFilter, categoryFilter, sortOrder]);
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Toggle dropdown
  const toggleDropdown = (dropdown: string) => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
    }
  };
  // Select service for viewing
  const viewService = (serviceId: string) => {
    setSelectedService(serviceId);
    setIsEditing(false);
    setEditedService(null);
  };
  // Edit service
  const editService = (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setSelectedService(serviceId);
      setEditedService({
        ...service
      });
      setIsEditing(true);
    }
  };
  // Save edited service
  const saveServiceChanges = () => {
    if (!editedService) return;
    setIsSaving(true);
    // Simulate API call delay
    setTimeout(() => {
      const updatedServices = services.map(service => {
        if (service.id === editedService.id) {
          return {
            ...editedService,
            lastUpdated: new Date().toISOString()
          };
        }
        return service;
      });
      setServices(updatedServices);
      setIsEditing(false);
      setIsSaving(false);
    }, 1000);
  };
  // Cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditedService(null);
  };
  // Delete service
  const deleteService = (serviceId: string) => {
    setServiceToDelete(serviceId);
    setShowDeleteConfirm(true);
  };
  // Confirm delete service
  const confirmDeleteService = () => {
    const updatedServices = services.filter(service => service.id !== serviceToDelete);
    setServices(updatedServices);
    setShowDeleteConfirm(false);
    setServiceToDelete('');
    // If the deleted service was selected, clear selection
    if (selectedService === serviceToDelete) {
      setSelectedService(null);
    }
  };
  // Create new service
  const createService = () => {
    if (newService.name.trim() === '' || newService.description.trim() === '') {
      return;
    }
    setIsSaving(true);
    // Simulate API call delay
    setTimeout(() => {
      const now = new Date();
      const newServiceObj = {
        id: `service-new-${Date.now()}`,
        name: newService.name,
        description: newService.description,
        status: newService.status,
        category: newService.category,
        mda: isSuperAdmin() ? newService.mda : getMdaName() || '',
        processingTime: newService.processingTime,
        fee: newService.fee,
        applicationCount: 0,
        createdDate: now.toISOString(),
        lastUpdated: now.toISOString(),
        requirements: newService.requirements.filter(r => r.trim() !== ''),
        steps: newService.steps.filter(s => s.trim() !== '')
      };
      setServices([newServiceObj, ...services]);
      setShowNewServiceModal(false);
      setIsSaving(false);
      setNewService({
        name: '',
        description: '',
        category: 'Business',
        status: 'Draft',
        mda: '',
        processingTime: 5,
        fee: 5000,
        requirements: [''],
        steps: ['']
      });
    }, 1000);
  };
  // Add requirement field to new service form
  const addRequirement = () => {
    setNewService({
      ...newService,
      requirements: [...newService.requirements, '']
    });
  };
  // Update requirement field in new service form
  const updateRequirement = (index: number, value: string) => {
    const updatedRequirements = [...newService.requirements];
    updatedRequirements[index] = value;
    setNewService({
      ...newService,
      requirements: updatedRequirements
    });
  };
  // Remove requirement field from new service form
  const removeRequirement = (index: number) => {
    const updatedRequirements = [...newService.requirements];
    updatedRequirements.splice(index, 1);
    setNewService({
      ...newService,
      requirements: updatedRequirements
    });
  };
  // Add step field to new service form
  const addStep = () => {
    setNewService({
      ...newService,
      steps: [...newService.steps, '']
    });
  };
  // Update step field in new service form
  const updateStep = (index: number, value: string) => {
    const updatedSteps = [...newService.steps];
    updatedSteps[index] = value;
    setNewService({
      ...newService,
      steps: updatedSteps
    });
  };
  // Remove step field from new service form
  const removeStep = (index: number) => {
    const updatedSteps = [...newService.steps];
    updatedSteps.splice(index, 1);
    setNewService({
      ...newService,
      steps: updatedSteps
    });
  };
  // Add requirement field to edited service form
  const addEditedRequirement = () => {
    if (!editedService) return;
    setEditedService({
      ...editedService,
      requirements: [...editedService.requirements, '']
    });
  };
  // Update requirement field in edited service form
  const updateEditedRequirement = (index: number, value: string) => {
    if (!editedService) return;
    const updatedRequirements = [...editedService.requirements];
    updatedRequirements[index] = value;
    setEditedService({
      ...editedService,
      requirements: updatedRequirements
    });
  };
  // Remove requirement field from edited service form
  const removeEditedRequirement = (index: number) => {
    if (!editedService) return;
    const updatedRequirements = [...editedService.requirements];
    updatedRequirements.splice(index, 1);
    setEditedService({
      ...editedService,
      requirements: updatedRequirements
    });
  };
  // Add step field to edited service form
  const addEditedStep = () => {
    if (!editedService) return;
    setEditedService({
      ...editedService,
      steps: [...editedService.steps, '']
    });
  };
  // Update step field in edited service form
  const updateEditedStep = (index: number, value: string) => {
    if (!editedService) return;
    const updatedSteps = [...editedService.steps];
    updatedSteps[index] = value;
    setEditedService({
      ...editedService,
      steps: updatedSteps
    });
  };
  // Remove step field from edited service form
  const removeEditedStep = (index: number) => {
    if (!editedService) return;
    const updatedSteps = [...editedService.steps];
    updatedSteps.splice(index, 1);
    setEditedService({
      ...editedService,
      steps: updatedSteps
    });
  };
  // Refresh services
  const refreshServices = () => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      let allServices = [];
      // If super admin, load all services
      // If MDA admin, only load services for their MDA
      if (isSuperAdmin()) {
        allServices = generateMockServices(50);
      } else {
        const mdaName = getMdaName();
        allServices = generateMockServices(20, mdaName);
      }
      setServices(allServices);
      setFilteredServices(allServices);
      // Calculate service stats
      const activeServices = allServices.filter((s: any) => s.status === 'Active').length;
      const draftServices = allServices.filter((s: any) => s.status === 'Draft').length;
      // Find most popular service
      let mostPopular = allServices[0];
      allServices.forEach((service: any) => {
        if (service.applicationCount > (mostPopular?.applicationCount || 0)) {
          mostPopular = service;
        }
      });
      // Calculate average fee
      const totalFee = allServices.reduce((sum: number, service: any) => sum + service.fee, 0);
      const averageFee = Math.round(totalFee / allServices.length);
      setServiceStats({
        total: allServices.length,
        active: activeServices,
        draft: draftServices,
        popular: mostPopular?.name || 'None',
        averageFee
      });
      setIsLoading(false);
      setSelectedService(null);
      setIsEditing(false);
      setEditedService(null);
    }, 1000);
  };
  // Get status badge class
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Inactive':
        return 'bg-red-100 text-red-800';
      case 'Draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'Under Review':
        return 'bg-blue-100 text-blue-800';
      case 'Archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-3395">
        <div className="flex flex-col items-center" data-id="element-3396">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4" data-id="element-3397"></div>
          <p className="text-gray-600" data-id="element-3398">Loading services...</p>
        </div>
      </div>;
  }
  return <div className="flex h-screen bg-gray-100" data-id="element-3399">
      {/* Sidebar - hidden on mobile unless toggled */}
      <div className={`${isSidebarOpen ? 'block' : 'hidden'} lg:block fixed inset-y-0 left-0 z-50 lg:relative lg:z-0`} data-id="element-3400">
        <AdminSidebar data-id="element-3401" />
      </div>
      {/* Backdrop for mobile sidebar */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-3402"></div>}
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-3403">
        <AdminHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} data-id="element-3404" />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-3405">
          {/* Page Header */}
          <div className="mb-6" data-id="element-3406">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-3407">
              Service Management
              {!isSuperAdmin() && <span className="ml-2 text-lg font-normal text-gray-600" data-id="element-3408">
                  ({getMdaName()})
                </span>}
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-3409">
              {isSuperAdmin() ? 'Manage and organize all services across MDAs' : `Manage and organize services for ${getMdaName()}`}
            </p>
          </div>
          {/* Service Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-id="element-3410">
            <div className="bg-white rounded-lg shadow p-4" data-id="element-3411">
              <div className="flex justify-between items-start" data-id="element-3412">
                <div data-id="element-3413">
                  <p className="text-sm font-medium text-gray-500" data-id="element-3414">
                    Total Services
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3415">
                    {serviceStats.total}
                  </p>
                </div>
                <div className="p-2 bg-blue-100 rounded-md" data-id="element-3416">
                  <LayersIcon size={20} className="text-blue-600" data-id="element-3417" />
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-600" data-id="element-3418">
                <span data-id="element-3419">Across {isSuperAdmin() ? 'all MDAs' : getMdaName()}</span>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4" data-id="element-3420">
              <div className="flex justify-between items-start" data-id="element-3421">
                <div data-id="element-3422">
                  <p className="text-sm font-medium text-gray-500" data-id="element-3423">
                    Active Services
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3424">
                    {serviceStats.active}
                  </p>
                </div>
                <div className="p-2 bg-green-100 rounded-md" data-id="element-3425">
                  <CheckCircleIcon size={20} className="text-green-600" data-id="element-3426" />
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-600" data-id="element-3427">
                <span data-id="element-3428">
                  {Math.round(serviceStats.active / serviceStats.total * 100)}
                  % of total services
                </span>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4" data-id="element-3429">
              <div className="flex justify-between items-start" data-id="element-3430">
                <div data-id="element-3431">
                  <p className="text-sm font-medium text-gray-500" data-id="element-3432">
                    Average Fee
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3433">
                    {formatCurrency(serviceStats.averageFee)}
                  </p>
                </div>
                <div className="p-2 bg-purple-100 rounded-md" data-id="element-3434">
                  <DollarSignIcon size={20} className="text-purple-600" data-id="element-3435" />
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-600" data-id="element-3436">
                <span data-id="element-3437">Per service application</span>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4" data-id="element-3438">
              <div className="flex justify-between items-start" data-id="element-3439">
                <div data-id="element-3440">
                  <p className="text-sm font-medium text-gray-500" data-id="element-3441">
                    Most Popular
                  </p>
                  <p className="text-lg font-bold text-gray-900 mt-1 truncate max-w-[220px]" data-id="element-3442">
                    {serviceStats.popular}
                  </p>
                </div>
                <div className="p-2 bg-yellow-100 rounded-md" data-id="element-3443">
                  <BarChart2Icon size={20} className="text-yellow-600" data-id="element-3444" />
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-600" data-id="element-3445">
                <span data-id="element-3446">Based on application count</span>
              </div>
            </div>
          </div>
          {/* Service Controls */}
          <div className="bg-white rounded-lg shadow mb-6" data-id="element-3447">
            <div className="p-4 border-b border-gray-200" data-id="element-3448">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-3449">
                {/* Search */}
                <div className="relative flex-1" data-id="element-3450">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3451">
                    <SearchIcon size={18} className="text-gray-400" data-id="element-3452" />
                  </div>
                  <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder="Search services by name, description, or category" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3453" />
                </div>
                {/* Filters */}
                <div className="flex flex-wrap gap-2" data-id="element-3454">
                  <div className="relative" data-id="element-3455">
                    <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={() => toggleDropdown('filter')} data-id="element-3456">
                      <FilterIcon size={18} className="mr-2" data-id="element-3457" />
                      Filters
                      <ChevronDownIcon size={18} className="ml-2" data-id="element-3458" />
                    </button>
                    {activeDropdown === 'filter' && <div className="origin-top-right absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-3459">
                        <div className="py-1 p-3" data-id="element-3460">
                          <div className="mb-3" data-id="element-3461">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3462">
                              Status
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-3463">
                              <option value="all" data-id="element-3464">All Statuses</option>
                              <option value="Active" data-id="element-3465">Active</option>
                              <option value="Inactive" data-id="element-3466">Inactive</option>
                              <option value="Draft" data-id="element-3467">Draft</option>
                              <option value="Under Review" data-id="element-3468">Under Review</option>
                              <option value="Archived" data-id="element-3469">Archived</option>
                            </select>
                          </div>
                          <div className="mb-3" data-id="element-3470">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3471">
                              Category
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-3472">
                              <option value="all" data-id="element-3473">All Categories</option>
                              <option value="Business" data-id="element-3474">Business</option>
                              <option value="Tax" data-id="element-3475">Tax</option>
                              <option value="Permits" data-id="element-3476">Permits</option>
                              <option value="Licenses" data-id="element-3477">Licenses</option>
                              <option value="Certificates" data-id="element-3478">Certificates</option>
                              <option value="Registration" data-id="element-3479">Registration</option>
                              <option value="Approvals" data-id="element-3480">Approvals</option>
                            </select>
                          </div>
                          <div className="mb-3" data-id="element-3481">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3482">
                              Sort By
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={sortOrder} onChange={e => setSortOrder(e.target.value)} data-id="element-3483">
                              <option value="newest" data-id="element-3484">Newest First</option>
                              <option value="oldest" data-id="element-3485">Oldest First</option>
                              <option value="name-asc" data-id="element-3486">Name (A-Z)</option>
                              <option value="name-desc" data-id="element-3487">Name (Z-A)</option>
                              <option value="fee-high" data-id="element-3488">
                                Fee (High to Low)
                              </option>
                              <option value="fee-low" data-id="element-3489">Fee (Low to High)</option>
                            </select>
                          </div>
                          <div className="flex justify-between pt-2" data-id="element-3490">
                            <button className="text-sm text-gray-700 hover:text-gray-900" onClick={() => {
                          setStatusFilter('all');
                          setCategoryFilter('all');
                          setSortOrder('newest');
                        }} data-id="element-3491">
                              Reset Filters
                            </button>
                            <button className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700" onClick={() => setActiveDropdown(null)} data-id="element-3492">
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>}
                  </div>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'all' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('all')} data-id="element-3493">
                    All
                  </button>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'active' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('active')} data-id="element-3494">
                    Active
                  </button>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'draft' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('draft')} data-id="element-3495">
                    Draft
                  </button>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'popular' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('popular')} data-id="element-3496">
                    Popular
                  </button>
                  <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={refreshServices} disabled={isLoading} data-id="element-3497">
                    <RefreshCwIcon size={18} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} data-id="element-3498" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>
            {/* Create Service Button */}
            {canCreateService && <div className="p-4 bg-gray-50 flex justify-end" data-id="element-3499">
                <button onClick={() => setShowNewServiceModal(true)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-3500">
                  <PlusIcon size={18} className="mr-2" data-id="element-3501" />
                  Create Service
                </button>
              </div>}
          </div>
          {/* Service Content */}
          <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-3502">
            {filteredServices.length === 0 ? <div className="p-8 text-center" data-id="element-3503">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-3504">
                  <LayersIcon size={24} className="text-gray-400" data-id="element-3505" />
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-3506">
                  No services found
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-3507">
                  {searchQuery ? 'Try adjusting your search or filter criteria.' : 'There are no services matching your current filters.'}
                </p>
                <div className="mt-6" data-id="element-3508">
                  <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" onClick={refreshServices} data-id="element-3509">
                    <RefreshCwIcon size={16} className="mr-2" data-id="element-3510" />
                    Refresh
                  </button>
                </div>
              </div> : <div className="flex flex-col md:flex-row h-full" data-id="element-3511">
                {/* Service List */}
                <div className={`w-full md:w-1/3 lg:w-1/4 border-r border-gray-200 ${selectedService ? 'hidden md:block' : ''}`} data-id="element-3512">
                  <div className="overflow-y-auto h-[calc(100vh-20rem)]" data-id="element-3513">
                    {filteredServices.map(service => <div key={service.id} className={`border-b border-gray-200 p-4 cursor-pointer hover:bg-gray-50 ${selectedService === service.id ? 'bg-green-50 border-l-4 border-l-green-500' : ''}`} onClick={() => viewService(service.id)} data-id="element-3514">
                        <div className="flex justify-between items-start mb-2" data-id="element-3515">
                          <h3 className="text-sm font-medium text-gray-900 line-clamp-1" data-id="element-3516">
                            {service.name}
                          </h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadgeClass(service.status)}`} data-id="element-3517">
                            {service.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2 line-clamp-2" data-id="element-3518">
                          {service.description}
                        </p>
                        <div className="flex justify-between items-center text-xs" data-id="element-3519">
                          <span className="text-gray-600" data-id="element-3520">
                            {service.category}
                          </span>
                          <span className="text-gray-600" data-id="element-3521">
                            {formatCurrency(service.fee)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2" data-id="element-3522">
                          <div className="flex items-center" data-id="element-3523">
                            <FileTextIcon size={12} className="text-gray-400 mr-1" data-id="element-3524" />
                            <span className="text-xs text-gray-500" data-id="element-3525">
                              {service.applicationCount} applications
                            </span>
                          </div>
                          {isSuperAdmin() && <span className="text-xs text-gray-500" data-id="element-3526">
                              {service.mda}
                            </span>}
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Service Detail */}
                {selectedService ? <div className="w-full md:w-2/3 lg:w-3/4 flex flex-col" data-id="element-3527">
                    {services.filter(s => s.id === selectedService).map(service => <div key={service.id} className="flex flex-col h-full" data-id="element-3528">
                          {/* Service Header */}
                          <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-3529">
                            <div className="flex items-center justify-between mb-2" data-id="element-3530">
                              <button className="md:hidden flex items-center text-gray-500 hover:text-gray-700" onClick={() => setSelectedService(null)} data-id="element-3531">
                                <ArrowLeftIcon size={16} className="mr-1" data-id="element-3532" />
                                Back
                              </button>
                              <div className="flex space-x-2" data-id="element-3533">
                                {canEditService && !isEditing && <button onClick={() => editService(service.id)} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-md hover:bg-blue-100" data-id="element-3534">
                                    <EditIcon size={14} className="inline mr-1" data-id="element-3535" />
                                    Edit
                                  </button>}
                                {canDeleteService && !isEditing && <button onClick={() => deleteService(service.id)} className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-md hover:bg-red-100" data-id="element-3536">
                                    <TrashIcon size={14} className="inline mr-1" data-id="element-3537" />
                                    Delete
                                  </button>}
                                {!isEditing && <div className="relative" data-id="element-3538">
                                    <button onClick={() => toggleDropdown('serviceActions')} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md hover:bg-gray-200" data-id="element-3539">
                                      <MoreHorizontalIcon size={14} data-id="element-3540" />
                                    </button>
                                    {activeDropdown === 'serviceActions' && <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-3541">
                                        <div className="py-1" data-id="element-3542">
                                          <button className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100" onClick={() => {
                              toggleDropdown('serviceActions');
                              // Export functionality would go here
                            }} data-id="element-3543">
                                            Export as PDF
                                          </button>
                                          <button className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100" onClick={() => {
                              toggleDropdown('serviceActions');
                              // Preview functionality would go here
                            }} data-id="element-3544">
                                            Preview Service
                                          </button>
                                          <button className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100" onClick={() => {
                              toggleDropdown('serviceActions');
                              // Analytics functionality would go here
                            }} data-id="element-3545">
                                            View Analytics
                                          </button>
                                        </div>
                                      </div>}
                                  </div>}
                              </div>
                            </div>
                            {isEditing ? <div className="mb-4" data-id="element-3546">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3547">
                                  Service Name
                                </label>
                                <input type="text" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={editedService?.name || ''} onChange={e => setEditedService({
                      ...editedService,
                      name: e.target.value
                    })} data-id="element-3548" />
                              </div> : <h2 className="text-lg font-medium text-gray-900" data-id="element-3549">
                                {service.name}
                              </h2>}
                            <div className="flex flex-wrap items-center gap-2 mt-2" data-id="element-3550">
                              {isEditing ? <select className="block rounded-md border border-gray-300 py-1 px-2 text-sm text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={editedService?.status || ''} onChange={e => setEditedService({
                      ...editedService,
                      status: e.target.value
                    })} data-id="element-3551">
                                  <option value="Active" data-id="element-3552">Active</option>
                                  <option value="Inactive" data-id="element-3553">Inactive</option>
                                  <option value="Draft" data-id="element-3554">Draft</option>
                                  <option value="Under Review" data-id="element-3555">
                                    Under Review
                                  </option>
                                  <option value="Archived" data-id="element-3556">Archived</option>
                                </select> : <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadgeClass(service.status)}`} data-id="element-3557">
                                  {service.status}
                                </span>}
                              {isEditing ? <select className="block rounded-md border border-gray-300 py-1 px-2 text-sm text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={editedService?.category || ''} onChange={e => setEditedService({
                      ...editedService,
                      category: e.target.value
                    })} data-id="element-3558">
                                  <option value="Business" data-id="element-3559">Business</option>
                                  <option value="Tax" data-id="element-3560">Tax</option>
                                  <option value="Permits" data-id="element-3561">Permits</option>
                                  <option value="Licenses" data-id="element-3562">Licenses</option>
                                  <option value="Certificates" data-id="element-3563">
                                    Certificates
                                  </option>
                                  <option value="Registration" data-id="element-3564">
                                    Registration
                                  </option>
                                  <option value="Approvals" data-id="element-3565">Approvals</option>
                                </select> : <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full" data-id="element-3566">
                                  {service.category}
                                </span>}
                              {isSuperAdmin() && !isEditing && <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full" data-id="element-3567">
                                  {service.mda}
                                </span>}
                              {isSuperAdmin() && isEditing && <select className="block rounded-md border border-gray-300 py-1 px-2 text-sm text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={editedService?.mda || ''} onChange={e => setEditedService({
                      ...editedService,
                      mda: e.target.value
                    })} data-id="element-3568">
                                  <option value="Ministry of Finance" data-id="element-3569">
                                    Ministry of Finance
                                  </option>
                                  <option value="Ministry of Lands and Surveys" data-id="element-3570">
                                    Ministry of Lands and Surveys
                                  </option>
                                  <option value="Ministry of Commerce and Industry" data-id="element-3571">
                                    Ministry of Commerce and Industry
                                  </option>
                                  <option value="Federal Road Safety Commission" data-id="element-3572">
                                    Federal Road Safety Commission
                                  </option>
                                  <option value="Ministry of Education" data-id="element-3573">
                                    Ministry of Education
                                  </option>
                                </select>}
                            </div>
                            <div className="mt-4" data-id="element-3574">
                              {isEditing ? <div className="mb-4" data-id="element-3575">
                                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3576">
                                    Description
                                  </label>
                                  <textarea className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" rows={3} value={editedService?.description || ''} onChange={e => setEditedService({
                        ...editedService,
                        description: e.target.value
                      })} data-id="element-3577"></textarea>
                                </div> : <p className="text-sm text-gray-600" data-id="element-3578">
                                  {service.description}
                                </p>}
                            </div>
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-id="element-3579">
                              <div data-id="element-3580">
                                <p className="text-gray-500" data-id="element-3581">Processing Time</p>
                                {isEditing ? <div className="flex items-center mt-1" data-id="element-3582">
                                    <input type="number" className="block w-20 rounded-md border border-gray-300 py-1 px-2 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" min="1" value={editedService?.processingTime || 1} onChange={e => setEditedService({
                          ...editedService,
                          processingTime: parseInt(e.target.value) || 1
                        })} data-id="element-3583" />
                                    <span className="ml-2" data-id="element-3584">days</span>
                                  </div> : <p className="font-medium text-gray-900 mt-1" data-id="element-3585">
                                    {service.processingTime}{' '}
                                    {service.processingTime === 1 ? 'day' : 'days'}
                                  </p>}
                              </div>
                              <div data-id="element-3586">
                                <p className="text-gray-500" data-id="element-3587">Application Fee</p>
                                {isEditing ? <div className="flex items-center mt-1" data-id="element-3588">
                                    <span className="mr-1" data-id="element-3589">₦</span>
                                    <input type="number" className="block w-32 rounded-md border border-gray-300 py-1 px-2 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" min="0" value={editedService?.fee || 0} onChange={e => setEditedService({
                          ...editedService,
                          fee: parseInt(e.target.value) || 0
                        })} data-id="element-3590" />
                                  </div> : <p className="font-medium text-gray-900 mt-1" data-id="element-3591">
                                    {formatCurrency(service.fee)}
                                  </p>}
                              </div>
                              <div data-id="element-3592">
                                <p className="text-gray-500" data-id="element-3593">Applications</p>
                                <p className="font-medium text-gray-900 mt-1" data-id="element-3594">
                                  {service.applicationCount.toLocaleString()}
                                </p>
                              </div>
                              <div data-id="element-3595">
                                <p className="text-gray-500" data-id="element-3596">Created On</p>
                                <p className="font-medium text-gray-900 mt-1" data-id="element-3597">
                                  {formatDate(service.createdDate)}
                                </p>
                              </div>
                              <div data-id="element-3598">
                                <p className="text-gray-500" data-id="element-3599">Last Updated</p>
                                <p className="font-medium text-gray-900 mt-1" data-id="element-3600">
                                  {formatDate(service.lastUpdated)}
                                </p>
                              </div>
                            </div>
                          </div>
                          {/* Service Details */}
                          <div className="flex-1 overflow-y-auto p-4" data-id="element-3601">
                            <div className="mb-6" data-id="element-3602">
                              <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="element-3603">
                                Requirements
                              </h3>
                              {isEditing ? <div className="space-y-2" data-id="element-3604">
                                  {editedService?.requirements.map((requirement: string, index: number) => <div key={index} className="flex items-center" data-id="element-3605">
                                        <input type="text" className="block flex-1 rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={requirement} onChange={e => updateEditedRequirement(index, e.target.value)} data-id="element-3606" />
                                        <button onClick={() => removeEditedRequirement(index)} className="ml-2 text-red-600 hover:text-red-800" data-id="element-3607">
                                          <XIcon size={18} data-id="element-3608" />
                                        </button>
                                      </div>)}
                                  <button onClick={addEditedRequirement} className="text-sm text-green-600 hover:text-green-800 flex items-center" data-id="element-3609">
                                    <PlusIcon size={16} className="mr-1" data-id="element-3610" />
                                    Add Requirement
                                  </button>
                                </div> : <ul className="list-disc pl-5 space-y-2" data-id="element-3611">
                                  {service.requirements.map((requirement: string, index: number) => <li key={index} className="text-sm text-gray-600" data-id="element-3612">
                                        {requirement}
                                      </li>)}
                                </ul>}
                            </div>
                            <div data-id="element-3613">
                              <h3 className="text-lg font-medium text-gray-900 mb-3" data-id="element-3614">
                                Application Process
                              </h3>
                              {isEditing ? <div className="space-y-2" data-id="element-3615">
                                  {editedService?.steps.map((step: string, index: number) => <div key={index} className="flex items-center" data-id="element-3616">
                                        <div className="flex-shrink-0 mr-3" data-id="element-3617">
                                          <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-medium" data-id="element-3618">
                                            {index + 1}
                                          </div>
                                        </div>
                                        <input type="text" className="block flex-1 rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={step} onChange={e => updateEditedStep(index, e.target.value)} data-id="element-3619" />
                                        <button onClick={() => removeEditedStep(index)} className="ml-2 text-red-600 hover:text-red-800" data-id="element-3620">
                                          <XIcon size={18} data-id="element-3621" />
                                        </button>
                                      </div>)}
                                  <button onClick={addEditedStep} className="text-sm text-green-600 hover:text-green-800 flex items-center" data-id="element-3622">
                                    <PlusIcon size={16} className="mr-1" data-id="element-3623" />
                                    Add Step
                                  </button>
                                </div> : <ol className="space-y-4" data-id="element-3624">
                                  {service.steps.map((step: string, index: number) => <li key={index} className="flex items-start" data-id="element-3625">
                                        <div className="flex-shrink-0 mr-3" data-id="element-3626">
                                          <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-medium" data-id="element-3627">
                                            {index + 1}
                                          </div>
                                        </div>
                                        <p className="text-sm text-gray-600" data-id="element-3628">
                                          {step}
                                        </p>
                                      </li>)}
                                </ol>}
                            </div>
                          </div>
                          {/* Edit Actions */}
                          {isEditing && <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3" data-id="element-3629">
                              <button onClick={cancelEditing} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-3630">
                                Cancel
                              </button>
                              <button onClick={saveServiceChanges} disabled={isSaving} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-3631">
                                {isSaving ? <>
                                    <RefreshCwIcon size={16} className="inline mr-2 animate-spin" data-id="element-3632" />
                                    Saving...
                                  </> : <>
                                    <SaveIcon size={16} className="inline mr-2" data-id="element-3633" />
                                    Save Changes
                                  </>}
                              </button>
                            </div>}
                        </div>)}
                  </div> : <div className="w-full md:w-2/3 lg:w-3/4 flex items-center justify-center p-8 bg-gray-50" data-id="element-3634">
                    <div className="text-center" data-id="element-3635">
                      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-3636">
                        <LayersIcon size={24} className="text-gray-400" data-id="element-3637" />
                      </div>
                      <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-3638">
                        No service selected
                      </h3>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-3639">
                        Select a service from the list to view details.
                      </p>
                    </div>
                  </div>}
              </div>}
          </div>
          {/* Help & Resources */}
          <div className="bg-white rounded-lg shadow p-4 mt-6" data-id="element-3640">
            <div className="flex justify-between items-center mb-4" data-id="element-3641">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-3642">
                Service Management Resources
              </h3>
              <button className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md hover:bg-gray-200" data-id="element-3643">
                <MoreHorizontalIcon size={14} data-id="element-3644" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3645">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-3646">
                <div className="flex items-start" data-id="element-3647">
                  <div className="p-2 bg-blue-100 rounded-md mr-3" data-id="element-3648">
                    <HelpCircleIcon size={20} className="text-blue-600" data-id="element-3649" />
                  </div>
                  <div data-id="element-3650">
                    <h4 className="text-sm font-medium text-gray-900 mb-1" data-id="element-3651">
                      Service Guidelines
                    </h4>
                    <p className="text-xs text-gray-600 mb-2" data-id="element-3652">
                      Learn best practices for creating and managing effective
                      government services.
                    </p>
                    <a href="#" className="text-xs text-blue-600 hover:text-blue-800 flex items-center" data-id="element-3653">
                      View Guidelines
                      <ArrowRightIcon size={14} className="ml-1" data-id="element-3654" />
                    </a>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-3655">
                <div className="flex items-start" data-id="element-3656">
                  <div className="p-2 bg-purple-100 rounded-md mr-3" data-id="element-3657">
                    <BarChart2Icon size={20} className="text-purple-600" data-id="element-3658" />
                  </div>
                  <div data-id="element-3659">
                    <h4 className="text-sm font-medium text-gray-900 mb-1" data-id="element-3660">
                      Service Analytics
                    </h4>
                    <p className="text-xs text-gray-600 mb-2" data-id="element-3661">
                      Access detailed analytics on service performance and user
                      engagement.
                    </p>
                    <a href="#" className="text-xs text-purple-600 hover:text-purple-800 flex items-center" data-id="element-3662">
                      View Analytics
                      <ArrowRightIcon size={14} className="ml-1" data-id="element-3663" />
                    </a>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-3664">
                <div className="flex items-start" data-id="element-3665">
                  <div className="p-2 bg-green-100 rounded-md mr-3" data-id="element-3666">
                    <FileTextIcon size={20} className="text-green-600" data-id="element-3667" />
                  </div>
                  <div data-id="element-3668">
                    <h4 className="text-sm font-medium text-gray-900 mb-1" data-id="element-3669">
                      Service Templates
                    </h4>
                    <p className="text-xs text-gray-600 mb-2" data-id="element-3670">
                      Use pre-designed templates to quickly create new services.
                    </p>
                    <a href="#" className="text-xs text-green-600 hover:text-green-800 flex items-center" data-id="element-3671">
                      Browse Templates
                      <ArrowRightIcon size={14} className="ml-1" data-id="element-3672" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* New Service Modal */}
      {showNewServiceModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-3673">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full" data-id="element-3674">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-3675">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-3676">
                Create New Service
              </h3>
            </div>
            <div className="p-6 max-h-[80vh] overflow-y-auto" data-id="element-3677">
              <div className="mb-4" data-id="element-3678">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3679">
                  Service Name
                </label>
                <input type="text" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" placeholder="Enter service name" value={newService.name} onChange={e => setNewService({
              ...newService,
              name: e.target.value
            })} data-id="element-3680" />
              </div>
              <div className="mb-4" data-id="element-3681">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3682">
                  Description
                </label>
                <textarea className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" rows={3} placeholder="Enter service description" value={newService.description} onChange={e => setNewService({
              ...newService,
              description: e.target.value
            })} data-id="element-3683"></textarea>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3684">
                <div data-id="element-3685">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3686">
                    Category
                  </label>
                  <select className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={newService.category} onChange={e => setNewService({
                ...newService,
                category: e.target.value
              })} data-id="element-3687">
                    <option value="Business" data-id="element-3688">Business</option>
                    <option value="Tax" data-id="element-3689">Tax</option>
                    <option value="Permits" data-id="element-3690">Permits</option>
                    <option value="Licenses" data-id="element-3691">Licenses</option>
                    <option value="Certificates" data-id="element-3692">Certificates</option>
                    <option value="Registration" data-id="element-3693">Registration</option>
                    <option value="Approvals" data-id="element-3694">Approvals</option>
                  </select>
                </div>
                <div data-id="element-3695">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3696">
                    Status
                  </label>
                  <select className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={newService.status} onChange={e => setNewService({
                ...newService,
                status: e.target.value
              })} data-id="element-3697">
                    <option value="Draft" data-id="element-3698">Draft</option>
                    <option value="Active" data-id="element-3699">Active</option>
                    <option value="Inactive" data-id="element-3700">Inactive</option>
                    <option value="Under Review" data-id="element-3701">Under Review</option>
                  </select>
                </div>
              </div>
              {isSuperAdmin() && <div className="mb-4" data-id="element-3702">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3703">
                    MDA
                  </label>
                  <select className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={newService.mda} onChange={e => setNewService({
              ...newService,
              mda: e.target.value
            })} data-id="element-3704">
                    <option value="" data-id="element-3705">Select MDA</option>
                    <option value="Ministry of Finance" data-id="element-3706">
                      Ministry of Finance
                    </option>
                    <option value="Ministry of Lands and Surveys" data-id="element-3707">
                      Ministry of Lands and Surveys
                    </option>
                    <option value="Ministry of Commerce and Industry" data-id="element-3708">
                      Ministry of Commerce and Industry
                    </option>
                    <option value="Federal Road Safety Commission" data-id="element-3709">
                      Federal Road Safety Commission
                    </option>
                    <option value="Ministry of Education" data-id="element-3710">
                      Ministry of Education
                    </option>
                  </select>
                </div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-3711">
                <div data-id="element-3712">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3713">
                    Processing Time (days)
                  </label>
                  <input type="number" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" min="1" value={newService.processingTime} onChange={e => setNewService({
                ...newService,
                processingTime: parseInt(e.target.value) || 1
              })} data-id="element-3714" />
                </div>
                <div data-id="element-3715">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3716">
                    Application Fee (₦)
                  </label>
                  <input type="number" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" min="0" value={newService.fee} onChange={e => setNewService({
                ...newService,
                fee: parseInt(e.target.value) || 0
              })} data-id="element-3717" />
                </div>
              </div>
              <div className="mb-6" data-id="element-3718">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3719">
                  Requirements
                </label>
                <div className="space-y-2" data-id="element-3720">
                  {newService.requirements.map((requirement, index) => <div key={index} className="flex items-center" data-id="element-3721">
                      <input type="text" className="block flex-1 rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" placeholder="Enter requirement" value={requirement} onChange={e => updateRequirement(index, e.target.value)} data-id="element-3722" />
                      <button onClick={() => removeRequirement(index)} className="ml-2 text-red-600 hover:text-red-800" data-id="element-3723">
                        <XIcon size={18} data-id="element-3724" />
                      </button>
                    </div>)}
                  <button onClick={addRequirement} className="text-sm text-green-600 hover:text-green-800 flex items-center" data-id="element-3725">
                    <PlusIcon size={16} className="mr-1" data-id="element-3726" />
                    Add Requirement
                  </button>
                </div>
              </div>
              <div className="mb-6" data-id="element-3727">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3728">
                  Application Process Steps
                </label>
                <div className="space-y-2" data-id="element-3729">
                  {newService.steps.map((step, index) => <div key={index} className="flex items-center" data-id="element-3730">
                      <div className="flex-shrink-0 mr-3" data-id="element-3731">
                        <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs font-medium" data-id="element-3732">
                          {index + 1}
                        </div>
                      </div>
                      <input type="text" className="block flex-1 rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" placeholder="Enter process step" value={step} onChange={e => updateStep(index, e.target.value)} data-id="element-3733" />
                      <button onClick={() => removeStep(index)} className="ml-2 text-red-600 hover:text-red-800" data-id="element-3734">
                        <XIcon size={18} data-id="element-3735" />
                      </button>
                    </div>)}
                  <button onClick={addStep} className="text-sm text-green-600 hover:text-green-800 flex items-center" data-id="element-3736">
                    <PlusIcon size={16} className="mr-1" data-id="element-3737" />
                    Add Step
                  </button>
                </div>
              </div>
              <div className="mb-6" data-id="element-3738">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3739">
                  Service Image (Optional)
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-3740">
                  <div className="space-y-1 text-center" data-id="element-3741">
                    <ImageIcon size={48} className="mx-auto text-gray-400" data-id="element-3742" />
                    <div className="flex text-sm text-gray-600" data-id="element-3743">
                      <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none" data-id="element-3744">
                        <span data-id="element-3745">Upload a file</span>
                        <input id="file-upload" name="file-upload" type="file" className="sr-only" data-id="element-3746" />
                      </label>
                      <p className="pl-1" data-id="element-3747">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-3748">
                      PNG, JPG, GIF up to 2MB
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3" data-id="element-3749">
                <button onClick={() => setShowNewServiceModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-3750">
                  Cancel
                </button>
                <button onClick={createService} disabled={newService.name.trim() === '' || newService.description.trim() === '' || isSuperAdmin() && newService.mda === '' || isSaving} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${newService.name.trim() === '' || newService.description.trim() === '' || isSuperAdmin() && newService.mda === '' || isSaving ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`} data-id="element-3751">
                  {isSaving ? <>
                      <RefreshCwIcon size={16} className="inline mr-2 animate-spin" data-id="element-3752" />
                      Creating...
                    </> : 'Create Service'}
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-3753">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-3754">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-3755">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-3756">
                Confirm Delete
              </h3>
            </div>
            <div className="p-6" data-id="element-3757">
              <div className="flex items-center mb-4" data-id="element-3758">
                <div className="flex-shrink-0 bg-red-100 rounded-full p-2" data-id="element-3759">
                  <AlertCircleIcon size={24} className="text-red-600" data-id="element-3760" />
                </div>
                <div className="ml-3" data-id="element-3761">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-3762">
                    Are you sure you want to delete this service?
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-3763">
                    This action cannot be undone. All service data will be
                    permanently removed.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-3764">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-3765">
                Cancel
              </button>
              <button onClick={confirmDeleteService} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-3766">
                Delete
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default ServiceManagement;